import {
  KEY_PREFIX,
  type StorageKey,
  type StoragePort,
  storageKeyName,
  VERSION_KEY,
} from '../../ports/storage';

/**
 * Shramba nad `localStorage` (in `sessionStorage` za časovnik spanja).
 *
 * **Časovnik spanja ne sme preživeti zaprtja zavihka** (T2 §3): sicer
 * uporabnik naslednji dan odpre aplikacijo in ta se mu po pol ure sama utiša.
 * Zato je `sleep` edini ključ v `sessionStorage`.
 *
 * Vsak dostop je v `try`: v zasebnem načinu Safarija `localStorage` obstaja,
 * a pisanje vrže. Aplikacija mora teči tudi tam — le brez trajnosti. Zato
 * napake tu **ne potujejo navzgor**; klicno mesto ne more storiti ničesar
 * pametnejšega, kot bi lahko storili tu.
 *
 * Zgodovina (`nw.history`) v to shrambo ne spada — je edini nabor, ki
 * neomejeno raste, in gre v IndexedDB v fazi 5 (T2 §3).
 */

/** Ključi, ki živijo le za sejo. */
const SESSION_KEYS: readonly StorageKey[] = ['sleep'];

function areaFor(key: StorageKey): Storage | null {
  try {
    return SESSION_KEYS.includes(key) ? sessionStorage : localStorage;
  } catch {
    return null;
  }
}

function readJson<T>(area: Storage | null, name: string): T | null {
  if (area === null) return null;
  try {
    const raw = area.getItem(name);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    // Pokvarjen zapis je za bralca isto kot noben zapis. Ne brišemo ga —
    // uporabnik ga lahko še vedno izvozi iz razvijalskih orodij (T2 §4, pravilo 4).
    return null;
  }
}

function writeJson(area: Storage | null, name: string, value: unknown): boolean {
  if (area === null) return false;
  try {
    area.setItem(name, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function createWebStorage(): StoragePort {
  return {
    load<T>(key: StorageKey): Promise<T | null> {
      return Promise.resolve(readJson<T>(areaFor(key), storageKeyName(key)));
    },

    save<T>(key: StorageKey, value: T): Promise<void> {
      writeJson(areaFor(key), storageKeyName(key), value);
      return Promise.resolve();
    },

    clear(key: StorageKey): Promise<void> {
      try {
        areaFor(key)?.removeItem(storageKeyName(key));
      } catch {
        // Brisanje, ki ne uspe, ni stanje, iz katerega bi se dalo okrevati.
      }
      return Promise.resolve();
    },

    readVersion(): Promise<number | null> {
      const value = readJson<unknown>(areaFor('settings'), VERSION_KEY);
      return Promise.resolve(typeof value === 'number' ? value : null);
    },

    writeVersion(version: number): Promise<void> {
      writeJson(areaFor('settings'), VERSION_KEY, version);
      return Promise.resolve();
    },

    backup(version: number, snapshot: unknown): Promise<void> {
      writeJson(areaFor('settings'), `${KEY_PREFIX}backup.${version}`, snapshot);
      return Promise.resolve();
    },
  };
}
